import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { getUserLoginEvents } from '@/services/api';
import { Card, CardBody } from '@/components/common/Card';
import DataTable from '@/components/common/DataTable';
import Select from '@/components/common/Select';
import Modal from '@/components/common/Modal';
import Badge from '@/components/common/Badge';
import { formatDateTime, formatStatus } from '@/utils/formatters';
import type { UserLoginEvent, LoginEventPlatform } from '@/types/admin';

const PAGE_SIZE = 50;

function platformBadge(platform: LoginEventPlatform | null) {
  if (!platform) return <Badge variant="gray">Unknown</Badge>;
  const map: Record<LoginEventPlatform, { variant: 'gray' | 'success' | 'info'; label: string }> = {
    ios: { variant: 'gray', label: 'iOS' },
    android: { variant: 'success', label: 'Android' },
    web: { variant: 'info', label: 'Web' },
    unknown: { variant: 'gray', label: 'Unknown' },
  };
  const cfg = map[platform];
  return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
}

function countryFlag(code: string | null | undefined): string {
  if (!code || code.length !== 2) return '';
  const codePoints = code
    .toUpperCase()
    .split('')
    .map(c => 0x1f1e6 + c.charCodeAt(0) - 'A'.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

export default function UserActivityLog() {
  const [page, setPage] = useState(1);
  const [userSearch, setUserSearch] = useState('');
  const [country, setCountry] = useState('');
  const [platform, setPlatform] = useState<LoginEventPlatform | ''>('');
  const [vpnOnly, setVpnOnly] = useState(false);
  const [selected, setSelected] = useState<UserLoginEvent | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['userLoginEvents', page, userSearch, country, platform, vpnOnly],
    queryFn: () =>
      getUserLoginEvents(page, PAGE_SIZE, {
        userSearch: userSearch || undefined,
        country: country || undefined,
        platform: platform || undefined,
        vpnOnly,
      }),
  });

  // Build country filter options from current page's data
  const countryOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [{ value: '', label: 'All Countries' }];
    (data?.events || []).forEach(e => {
      if (e.country_code && !seen.has(e.country_code)) {
        seen.add(e.country_code);
        opts.push({
          value: e.country_code,
          label: `${countryFlag(e.country_code)} ${e.country || e.country_code}`,
        });
      }
    });
    return opts;
  }, [data]);

  const platformOptions = [
    { value: '', label: 'All Platforms' },
    { value: 'ios', label: 'iOS' },
    { value: 'android', label: 'Android' },
    { value: 'web', label: 'Web' },
    { value: 'unknown', label: 'Unknown' },
  ];

  const columns = useMemo<ColumnDef<UserLoginEvent>[]>(
    () => [
      {
        accessorKey: 'created_at',
        header: 'Time',
        cell: ({ row }) => (
          <span className="text-sm">{formatDateTime(row.original.created_at)}</span>
        ),
      },
      {
        id: 'user',
        header: 'User',
        cell: ({ row }) => {
          const e = row.original;
          const label = e.user_full_name || e.user_email || e.user_phone || e.user_id.slice(0, 8);
          return (
            <Link
              to={`/users/${e.user_id}`}
              className="text-brand hover:text-brand-dark text-sm font-medium"
            >
              {label}
            </Link>
          );
        },
      },
      {
        accessorKey: 'platform',
        header: 'Platform',
        cell: ({ row }) => platformBadge(row.original.platform),
      },
      {
        accessorKey: 'ip_address',
        header: 'IP',
        cell: ({ row }) => (
          <span className="font-mono text-sm">{row.original.ip_address || '-'}</span>
        ),
      },
      {
        id: 'location',
        header: 'Location',
        cell: ({ row }) => {
          const e = row.original;
          if (!e.country_code && !e.city) return <span className="text-gray-400">-</span>;
          const parts = [e.city, e.region].filter(Boolean).join(', ');
          return (
            <span className="text-sm">
              {e.country_code && <span className="mr-1">{countryFlag(e.country_code)}</span>}
              {parts || e.country || e.country_code}
            </span>
          );
        },
      },
      {
        accessorKey: 'isp',
        header: 'ISP',
        cell: ({ row }) => (
          <span className="text-sm text-gray-600 truncate max-w-[200px] inline-block">
            {row.original.isp || '-'}
          </span>
        ),
      },
      {
        id: 'flags',
        header: 'Flags',
        cell: ({ row }) =>
          row.original.is_vpn ? <Badge variant="danger">VPN / Hosting</Badge> : (
            <span className="text-gray-400 text-xs">—</span>
          ),
      },
      {
        accessorKey: 'login_method',
        header: 'Method',
        cell: ({ row }) =>
          row.original.login_method ? (
            <Badge variant="gray" size="sm">{formatStatus(row.original.login_method)}</Badge>
          ) : (
            <span className="text-gray-400 text-xs">-</span>
          ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">User Activity Log</h1>
        <p className="mt-1 text-sm text-gray-500">
          Login events with IP address, geolocation, and device platform — use this to investigate
          suspicious activity or track users acting maliciously.
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardBody padding="sm">
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap items-stretch sm:items-center">
            <input
              type="text"
              value={userSearch}
              onChange={e => { setPage(1); setUserSearch(e.target.value); }}
              placeholder="Search name, email, phone, or IP…"
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent w-full sm:w-72"
            />
            <Select
              value={country}
              onChange={(v: string) => { setPage(1); setCountry(v); }}
              options={countryOptions}
              className="w-full sm:w-48"
              aria-label="Filter by country"
            />
            <Select
              value={platform}
              onChange={(v: string) => { setPage(1); setPlatform(v as LoginEventPlatform | ''); }}
              options={platformOptions}
              className="w-full sm:w-40"
              aria-label="Filter by platform"
            />
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={vpnOnly}
                onChange={e => { setPage(1); setVpnOnly(e.target.checked); }}
                className="w-4 h-4 rounded border-gray-300 text-brand focus:ring-brand"
              />
              VPN/Hosting only
            </label>
          </div>
        </CardBody>
      </Card>

      {/* Table */}
      <Card>
        <CardBody padding="none">
          <DataTable
            data={data?.events || []}
            columns={columns}
            isLoading={isLoading}
            page={page}
            pageSize={PAGE_SIZE}
            totalCount={data?.total}
            onPageChange={setPage}
            onRowClick={setSelected}
          />
        </CardBody>
      </Card>

      {/* Detail modal */}
      <Modal
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        title="Login Event Details"
        size="lg"
      >
        {selected && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Time" value={formatDateTime(selected.created_at)} />
              <Field label="User"
                value={
                  <Link to={`/users/${selected.user_id}`} className="text-brand hover:text-brand-dark">
                    {selected.user_full_name || selected.user_email || selected.user_phone || selected.user_id}
                  </Link>
                }
              />
              <Field label="Platform" value={platformBadge(selected.platform)} />
              <Field label="Login Method" value={selected.login_method ? formatStatus(selected.login_method) : '-'} />
              <Field label="IP Address" value={<span className="font-mono">{selected.ip_address || '-'}</span>} />
              <Field label="User Agent" value={<span className="font-mono text-xs break-all">{selected.user_agent || '-'}</span>} />
              <Field label="City" value={selected.city || '-'} />
              <Field label="Region" value={selected.region || '-'} />
              <Field
                label="Country"
                value={
                  <span>
                    {selected.country_code && <span className="mr-1">{countryFlag(selected.country_code)}</span>}
                    {selected.country || selected.country_code || '-'}
                  </span>
                }
              />
              <Field label="ISP" value={selected.isp || '-'} />
              <Field
                label="VPN / Hosting"
                value={selected.is_vpn ? <Badge variant="danger">Yes</Badge> : <span className="text-gray-500">No</span>}
              />
              <Field
                label="Coordinates"
                value={
                  selected.latitude && selected.longitude ? (
                    <a
                      href={`https://www.google.com/maps?q=${selected.latitude},${selected.longitude}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-brand hover:text-brand-dark"
                    >
                      {selected.latitude.toFixed(4)}, {selected.longitude.toFixed(4)} (open map)
                    </a>
                  ) : (
                    '-'
                  )
                }
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <div className="text-gray-900 text-sm mt-0.5">{value}</div>
    </div>
  );
}
